// Moved verbatim from `src/hooks/query/useQueryCapabilities.ts` (now a pure
// shim) with IP2 (ARCFE-956). Token reads severed onto the `useAuthToken`
// seam; the module-scope cache commands run against the seam's queryClient
// (the same app singleton their legacy `@/utils/reactQueryClient` import
// resolved to). The `!!token && !!userData ? queryFn : skipToken` gate is
// frozen behaviour on all three hooks.
import { skipToken, useQuery } from '@tanstack/react-query';

import {
  GET_CAPABILITIES,
  GET_CAPABILITY_AUTOCOMPLETE,
  GET_USER_CAPABILITIES,
} from '../../contracts/keys';
import { type CapabilityWithUserConfigListResponse } from '../../contracts/types/user-capabilities.types';
import {
  getCapabilities,
  getCapabilityAutocomplete,
  getUserCapabilities,
} from '../api/user';
import { getWorkspaceDataConfig } from '../config';
import { useQueryUser } from './useQueryUser';

export { GET_CAPABILITIES, GET_CAPABILITY_AUTOCOMPLETE, GET_USER_CAPABILITIES };

/**
 * Get cached user capabilities data
 */
export const getCurrentUserCapabilities = () =>
  getWorkspaceDataConfig().queryClient.getQueryData<CapabilityWithUserConfigListResponse>(
    [GET_USER_CAPABILITIES]
  );

/**
 * Invalidate user capabilities cache
 */
export const invalidateUserCapabilities = async () =>
  await getWorkspaceDataConfig().queryClient.invalidateQueries({
    queryKey: [GET_USER_CAPABILITIES],
  });

/**
 * Hook to fetch all capability definitions
 */
export function useQueryCapabilities() {
  const token = getWorkspaceDataConfig().useAuthToken();
  const { data: userData } = useQueryUser();

  return useQuery({
    queryKey: [GET_CAPABILITIES],
    queryFn:
      !!token && !!userData
        ? async () => {
            const { data } = await getCapabilities();
            return data;
          }
        : skipToken,
  });
}

/**
 * Hook to fetch user's capability configurations
 */
export function useQueryUserCapabilities() {
  const token = getWorkspaceDataConfig().useAuthToken();
  const { data: userData } = useQueryUser();

  return useQuery({
    queryKey: [GET_USER_CAPABILITIES],
    queryFn:
      !!token && !!userData
        ? async () => {
            const { data } = await getUserCapabilities();
            return data;
          }
        : skipToken,
  });
}

/**
 * Hook to fetch capability autocomplete suggestions
 */
export function useQueryCapabilityAutocomplete(query: string) {
  const token = getWorkspaceDataConfig().useAuthToken();
  const { data: userData } = useQueryUser();

  return useQuery({
    queryKey: [GET_CAPABILITY_AUTOCOMPLETE, query],
    queryFn:
      !!token && !!userData
        ? async () => {
            const { data } = await getCapabilityAutocomplete(query);
            return data;
          }
        : skipToken,
    // Short stale time for autocomplete
    staleTime: 30 * 1000, // 30 seconds
  });
}
